import { DOCUMENT, Location } from '@angular/common';
import { DestroyRef, Injectable, InjectionToken, NgZone, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HammerGestureConfig } from '@angular/platform-browser';

import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import fastdom from 'fastdom';
import { TouchMouseInput } from 'hammerjs';
import { delay, filter, fromEvent, merge } from 'rxjs';

import { GestureOptions, PopupAction } from '../../common/modal-window.component';
import { OpenedModalsStorageService } from '../opened-modals-storage.service';
import { ModalGestureAnimationService } from './modal-gesture-animation.service';
import { ModalGestureBackgroundService } from './modal-gesture-background.service';
import { ModalGestureHelperService } from './modal-gesture-helper.service';
import { ModalGestureIsClosingService } from './modal-gesture-is-closing.service';
import { ModalGesturePanUtility } from './modal-gesture-pan-utility';
import { ModalGestureQueriesService } from './modal-gesture-queries.service';
import {
    BETSLIP_BORDER_RADIUS_CHANGE_THRESHOLD,
    HORIZONTAL_SCROLL_CONTAINER_SELECTOR,
    LEFT_EDGE_THRESHOLD,
    MIN_BACKGROUND_BRIGHTNESS,
    PANEND_CLOSE_DURATION_HORIZONTAL,
    PANEND_CLOSE_EASING_HORIZONTAL,
    PANEND_REOPEN_DURATION_HORIZONTAL,
    PANEND_REOPEN_EASING_HORIZONTAL,
    PAN_BUFFER,
    PAN_THRESHOLD,
    TRIGGER_PANEND_AFTER_TOUCHEND_MS,
} from './modal-gesture.constants';
import { Background } from './modal-gesture.model';

export const HORIZONTAL_GESTURE_CONFIG = new InjectionToken<HammerGestureConfig>('ModalGestureHorizontalConfig');

@Injectable()
export class ModalGestureHorizontalConfig extends HammerGestureConfig {
    override overrides = {
        pan: {
            threshold: PAN_THRESHOLD,
            direction: Hammer.DIRECTION_ALL,
        },
    };

    override options = {
        inputClass: TouchMouseInput,
    };
}

@Injectable()
export class ModalHorizontalGestureService {
    #isDragging = false;
    #isAnimating = false;
    #isPanUpdateScheduled = false;
    #currentPosition = 0;
    #currentBackgroundPan = 0;
    #currentBackgroundBrightness = 0;
    #modalWindow: HTMLElement;
    #background: Background;
    #modalBody: HTMLElement | null;
    #betslipBar: HTMLElement | null;
    #previousUrl: string | undefined;

    readonly #ngZone = inject(NgZone);
    readonly #location = inject(Location);
    readonly #renderer = inject(Renderer2);
    readonly #document = inject(DOCUMENT);
    readonly #destroyRef = inject(DestroyRef);
    readonly #openedModals = inject(OpenedModalsStorageService);
    readonly #routerEventsService = inject(RouterEventsService);
    readonly #modalGestureQueries = inject(ModalGestureQueriesService);
    readonly #modalGestureAnimationService = inject(ModalGestureAnimationService);
    readonly #modalGestureBackgroundService = inject(ModalGestureBackgroundService);
    readonly #modalGestureClosingCalculator = inject(ModalGestureIsClosingService);
    readonly #modalGestureHelper = inject(ModalGestureHelperService);
    readonly #hammerGestureConfig = inject(HORIZONTAL_GESTURE_CONFIG);

    constructor() {
        this.#destroyRef.onDestroy(() => {
            this.teardown();
        });
    }

    addGestures(modalWindow: unknown, { enabled }: GestureOptions): void {
        if (!enabled || !(modalWindow instanceof HTMLElement)) {
            return;
        }

        this.initialize(modalWindow);
        this.setGestureStyles();
        this.animateSlideInFromRight();
    }

    private teardown(): void {
        this.resetBackgroundPan();
    }

    private initialize(modalWindow: HTMLElement): void {
        this.#modalWindow = modalWindow;
        this.#modalBody = this.#modalGestureQueries.getModalBody();
        this.#background = this.#modalGestureBackgroundService.getBackground();
        this.#previousUrl = this.#modalGestureHelper.setPreviousUrl(this.#routerEventsService.urlHistory);
        this.#betslipBar = this.#modalGestureQueries.getBetslipBar();
    }

    private setGestureStyles(): void {
        this.#modalGestureHelper.setBackdropTransparent();

        this.#renderer.setStyle(this.#modalWindow, 'will-change', `transform`);

        this.#background?.elements.forEach((element) => {
            this.#renderer.setStyle(element, 'will-change', `transform, translate, filter`);
        });
    }

    private animateSlideInFromRight(): void {
        /**
         * Set the modal to the animation's starting position before the animation starts. This prevents
         * the modal appearing briefly on creation before animating in, especially on slow devices.
         */
        this.setModalPan(this.#modalGestureHelper.screenWidth);

        this.#modalGestureAnimationService.slideInFromRight({ background: this.#background, modalWindow: this.#modalWindow }).finally(() => {
            this.#isAnimating = false;

            this.resetModalPan();

            this.#ngZone.runOutsideAngular(() => {
                this.handlePanEvents(this.#modalWindow);
            });
        });
    }

    private handlePanEvents(panElement: HTMLElement): void {
        const hammer = this.#hammerGestureConfig.buildHammer(panElement);

        this.#destroyRef.onDestroy(() => {
            hammer.destroy?.();
        });

        fromEvent<HammerInput>(hammer, 'panstart')
            .pipe(
                filter(
                    (pan) =>
                        !this.#isDragging &&
                        !this.#isAnimating &&
                        pan.direction === Hammer.DIRECTION_RIGHT &&
                        pan.center.x - pan.deltaX <= LEFT_EDGE_THRESHOLD &&
                        !this.isHorizontallyScrollable(pan.srcEvent.target),
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe((pan) => this.handlePanstart(pan));

        fromEvent<HammerInput>(hammer, 'panmove')
            .pipe(
                filter(() => this.#isDragging),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe((pan) => this.handlePanmove(pan));

        merge(
            fromEvent<HammerInput>(hammer, 'panend'),
            fromEvent<TouchEvent>(panElement, 'touchend', { capture: true }).pipe(delay(TRIGGER_PANEND_AFTER_TOUCHEND_MS)),
            fromEvent<TouchEvent>(panElement, 'touchcancel', { capture: true }).pipe(delay(TRIGGER_PANEND_AFTER_TOUCHEND_MS)),
        )
            .pipe(
                filter(() => this.#isDragging),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe((pan) => this.handlePanend(pan));
    }

    // This could be potentially be cached in future to avoid a reflow on panstart
    private isHorizontallyScrollable(element: EventTarget | null): boolean {
        if (!(element instanceof HTMLElement)) {
            return false;
        }

        const scrollContainer = element.closest(HORIZONTAL_SCROLL_CONTAINER_SELECTOR);

        return Boolean(scrollContainer && scrollContainer.scrollWidth > scrollContainer.clientWidth);
    }

    private handlePanstart(pan: HammerInput): void {
        (this.#document.activeElement as HTMLElement)?.blur();

        this.#isDragging = true;

        fastdom.mutate(() => {
            this.setPanStyles();
        });

        // Ensure we pan using the first event given, since panstart/panmove do not come as a pair (can be 1 frame between)
        this.handlePanmove(pan);
    }

    private setPanStyles(): void {
        this.#modalGestureHelper.blockScrolling(this.#modalBody);
        this.#modalGestureHelper.disableBackgroundTransitions(this.#background);
        this.#modalGestureHelper.disableConflictingBackgroundAnimations(this.#background);
        this.#modalGestureHelper.setEventBackdropTransparent();
    }

    private handlePanmove({ deltaX }: HammerInput): void {
        this.#currentPosition = deltaX - PAN_BUFFER;

        if (!this.#isPanUpdateScheduled) {
            fastdom.mutate(() => {
                this.setPan(this.#currentPosition);

                this.#isPanUpdateScheduled = false;
            });

            this.#isPanUpdateScheduled = true;
        }

        fastdom.mutate(() => {
            this.handleBetslipBorderRadiusChange();

            if (this.#currentPosition + PAN_BUFFER >= this.#modalGestureHelper.viewportWidth) {
                this.animatePanendClose(this.#currentPosition);
            }
        });
    }

    private handleBetslipBorderRadiusChange(): void {
        if (this.#currentPosition <= BETSLIP_BORDER_RADIUS_CHANGE_THRESHOLD) {
            this.#modalGestureHelper.setBetslipBarBorderRadius('flat', this.#betslipBar);
        } else {
            this.#modalGestureHelper.setBetslipBarBorderRadius('rounded', this.#betslipBar);
        }
    }

    private setPan(panX = 0): void {
        if (this.#isAnimating) {
            return;
        }

        const panWithinScreen = Math.max(0, Math.min(panX, this.#modalGestureHelper.viewportWidth));

        this.setModalPan(panWithinScreen);
        this.setBackgroundPan(panWithinScreen);
    }

    private setModalPan(panX = 0): void {
        this.#renderer.setStyle(this.#modalWindow, 'transform', `translate3d(${panX}px, 0, 0)`);
    }

    private setBackgroundPan(panX: number): void {
        if (this.#openedModals.count > 1) {
            return;
        }

        this.#currentBackgroundPan = ModalGesturePanUtility.getBackgroundPanHorizontal(panX, this.#modalGestureHelper.viewportWidth);
        this.#currentBackgroundBrightness = ModalGesturePanUtility.getBackgroundBrightnessHorizontal(panX, this.#modalGestureHelper.viewportWidth);

        this.#background?.elements.forEach((element) => {
            this.#renderer.setStyle(element, 'filter', `brightness(${this.#currentBackgroundBrightness})`);
            this.#renderer.setStyle(element, 'transform', `translate3d(-${this.#currentBackgroundPan}px, 0, 0)`);
        });
    }

    private handlePanend(pan: HammerInput | TouchEvent): void {
        const { deltaX, velocity } = this.#modalGestureHelper.getHammerInput(pan, 'horizontal');

        this.#isDragging = false;

        const currentPosition = deltaX - PAN_BUFFER;

        if (currentPosition <= 0) {
            fastdom.mutate(() => {
                this.resetPanStyles();
            });

            return;
        }

        fastdom.mutate(() => {
            this.animatePanend(currentPosition, velocity);
        });
    }

    private resetPanStyles(): void {
        this.resetModalPan();
        this.resetBackgroundPan();
        this.#modalGestureHelper.setBetslipBarBorderRadius('rounded');
    }

    private resetModalPan(): void {
        this.#renderer.setStyle(this.#modalWindow, 'transform', 'translate3d(0px, 0, 0)');

        this.#modalGestureHelper.unblockScrolling(this.#modalBody);
    }

    private resetBackgroundPan(): void {
        this.#background?.elements.forEach((element) => {
            this.#renderer.removeStyle(element, 'filter');
            this.#renderer.removeStyle(element, 'transform');
        });

        this.#modalGestureHelper.enableBackgroundTransitions(this.#background);
        this.#modalGestureHelper.enableConflictingBackgroundAnimations(this.#background);
    }

    private animatePanend(deltaX: number, velocity: number): void {
        if (this.#modalGestureClosingCalculator.isModalClosingHorizontal(deltaX, velocity)) {
            this.animatePanendClose(deltaX);
        } else {
            this.animatePanendReopen(deltaX);
        }
    }

    private animatePanendClose(deltaX: number): void {
        this.#isAnimating = true;

        this.#modalGestureAnimationService
            .slideModalHorizontal(
                {
                    from: `${deltaX}px`,
                    to: `${this.#modalGestureHelper.viewportWidth}px`,
                    duration: PANEND_CLOSE_DURATION_HORIZONTAL,
                    easing: PANEND_CLOSE_EASING_HORIZONTAL,
                },
                this.#modalWindow,
            )
            ?.finally(() => {
                this.#isAnimating = false;

                this.close();
            });

        this.#modalGestureAnimationService
            .slideBackgroundHorizontal(
                {
                    from: `${this.#currentBackgroundPan}px`,
                    to: '0px',
                    fromBrightness: `${this.#currentBackgroundBrightness}`,
                    toBrightness: '1',
                    duration: PANEND_CLOSE_DURATION_HORIZONTAL,
                    easing: PANEND_CLOSE_EASING_HORIZONTAL,
                },
                this.#background,
            )
            .finally(() => {
                this.resetBackgroundPan();
            });
    }

    private animatePanendReopen(deltaX: number): void {
        this.#isAnimating = true;

        this.#modalGestureHelper.setBetslipBarBorderRadius('flat');

        this.#modalGestureAnimationService
            .slideModalHorizontal(
                {
                    from: `${deltaX}px`,
                    to: `0px`,
                    duration: PANEND_REOPEN_DURATION_HORIZONTAL,
                    easing: PANEND_REOPEN_EASING_HORIZONTAL,
                },
                this.#modalWindow,
            )
            ?.finally(() => {
                this.#isAnimating = false;

                this.resetModalPan();

                this.#modalGestureHelper.setBetslipBarBorderRadius('rounded');
            });

        this.#modalGestureAnimationService
            .slideBackgroundHorizontal(
                {
                    from: `${this.#currentBackgroundPan}px`,
                    to: `${ModalGesturePanUtility.getBackgroundPanHorizontalMax(this.#modalGestureHelper.viewportWidth)}px`,
                    fromBrightness: `${this.#currentBackgroundBrightness}`,
                    toBrightness: `${MIN_BACKGROUND_BRIGHTNESS}`,
                    duration: PANEND_REOPEN_DURATION_HORIZONTAL,
                    easing: PANEND_REOPEN_EASING_HORIZONTAL,
                },
                this.#background,
            )
            .finally(() => {
                this.resetBackgroundPan();
            });
    }

    private close(): void {
        this.#modalGestureHelper.getActiveModal()?.close({ closeAction: PopupAction.CloseByPan });

        if (this.#previousUrl) {
            this.#location.replaceState(this.#previousUrl);
        }
    }
}
